import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Form } from "@unform/web";
import intl from "react-intl-universal";
import { TbArrowLeft, TbClipboardText, TbX } from "react-icons/tb";
import { cleanTaxIDMask, handleTaxIDInput } from "../../../Utils";
import {
  formatPixKeyInput,
  resolvePixKeyApiType,
  validatePixKey,
} from "../../../Utils/detectPixKeyType";
import { useToast } from "../../../Hooks/toast";
import { PIX_KEY_TYPE_OPTIONS } from "./constants";
import { PixTransferModeSelector } from "./PixTransferModeSelector";
import { PixContactPickerPanel, PixContactPickerTrigger } from "./PixContactPicker";

const DEFAULT_KEY_TYPE = "cpf";

const resolveContactKeyType = (contact) => {
  if (!contact) return DEFAULT_KEY_TYPE;
  return (
    resolvePixKeyApiType(contact.pixKeyType, contact.pixKeyValue || contact.pixKeyDisplay) ||
    DEFAULT_KEY_TYPE
  );
};

export const PixDestinationForm = ({
  transferMode,
  onTransferModeChange,
  initialDraft,
  onContinue,
  formId,
  hideFooter = false,
  onBack,
  onSubViewChange,
  decoding = false,
  decodeError = null,
}) => {
  const history = useHistory();
  const { addToast } = useToast();
  const handleBack = onBack ?? (() => history.push("/pix"));

  const [selectedKeyType, setSelectedKeyType] = useState(
    resolvePixKeyApiType(initialDraft?.pixKeyType, initialDraft?.keyValue) || DEFAULT_KEY_TYPE
  );
  const [keyInput, setKeyInput] = useState(initialDraft?.keyDisplay || "");
  const [qrCode, setQrCode] = useState(initialDraft?.qrCode || "");
  const [documentToValidate, setDocumentToValidate] = useState(
    initialDraft?.documentToValidateDisplay || ""
  );
  const [selectedContactId, setSelectedContactId] = useState(initialDraft?.contactId || null);
  const [selectedContact, setSelectedContact] = useState(
    initialDraft?.contactName
      ? {
          name: initialDraft.contactName,
          bankName: initialDraft.bankName,
        }
      : null
  );
  const [keyError, setKeyError] = useState("");
  const [contactsOpen, setContactsOpen] = useState(false);

  const isKeyMode = transferMode === "key";
  const selectedTypeMeta = useMemo(
    () => PIX_KEY_TYPE_OPTIONS.find((item) => item.key === selectedKeyType) || PIX_KEY_TYPE_OPTIONS[0],
    [selectedKeyType]
  );

  useEffect(() => {
    setKeyError("");
  }, [transferMode]);

  useEffect(() => {
    if (decodeError) {
      setKeyError("");
    }
  }, [decodeError]);

  useEffect(() => {
    onSubViewChange?.(contactsOpen ? "contacts" : "destino");
  }, [contactsOpen, onSubViewChange]);

  useEffect(() => {
    return () => onSubViewChange?.("destino");
  }, [onSubViewChange]);

  const showClearKey = Boolean(keyInput) || Boolean(selectedContactId);

  const handleKeyTypeChange = useCallback((nextType) => {
    setSelectedKeyType(nextType);
    setKeyInput("");
    setKeyError("");
    setSelectedContactId(null);
    setSelectedContact(null);
  }, []);

  const handleKeyChange = useCallback(
    (e) => {
      setKeyInput(formatPixKeyInput(e.target.value, selectedKeyType));
      setKeyError("");
      setSelectedContactId(null);
      setSelectedContact(null);
    },
    [selectedKeyType]
  );

  const handleClearKey = useCallback(() => {
    setKeyInput("");
    setSelectedContactId(null);
    setSelectedContact(null);
    setKeyError("");
  }, []);

  const handlePasteQrCode = useCallback(async () => {
    try {
      if (!navigator.clipboard?.readText) {
        throw new Error("clipboard_unavailable");
      }
      const text = (await navigator.clipboard.readText()).trim();
      if (!text) {
        addToast({
          type: "info",
          title: intl.get("pix.transfer.pasteEmptyTitle"),
          description: intl.get("pix.transfer.pasteEmptyDescription"),
        });
        return;
      }
      setQrCode(text);
      setKeyError("");
    } catch {
      addToast({
        type: "error",
        title: intl.get("pix.transfer.pasteErrorTitle"),
        description: intl.get("pix.transfer.pasteErrorDescription"),
      });
    }
  }, [addToast]);

  const handleContactSelect = useCallback(
    (contact) => {
      if (transferMode !== "key") {
        onTransferModeChange("key");
      }

      const type = resolveContactKeyType(contact);
      setSelectedKeyType(type);
      setSelectedContactId(contact.id);
      setSelectedContact({ name: contact.name, bankName: contact.bankName });
      setKeyInput(formatPixKeyInput(contact.pixKeyDisplay || contact.pixKeyValue || "", type));
      setKeyError("");
      setContactsOpen(false);
    },
    [transferMode, onTransferModeChange]
  );

  const handleSubmit = useCallback(() => {
    if (decoding) return;

    if (isKeyMode) {
      const result = validatePixKey(keyInput, selectedKeyType);
      if (!result.valid) {
        setKeyError(result.message);
        return;
      }

      onContinue({
        mode: "key",
        pixKeyType: result.type,
        keyValue: result.value,
        keyDisplay: keyInput,
        documentToValidate: cleanTaxIDMask(documentToValidate) || undefined,
        documentToValidateDisplay: documentToValidate,
        contactId: selectedContactId || undefined,
        contactName: selectedContact?.name,
        bankName: selectedContact?.bankName,
      });
      return;
    }

    const trimmed = qrCode.trim();
    if (!trimmed) {
      setKeyError(intl.get("pix.transfer.copyPasteRequired"));
      return;
    }

    onContinue({
      mode: "qrcode",
      qrCode: trimmed,
    });
  }, [
    decoding,
    isKeyMode,
    keyInput,
    selectedKeyType,
    qrCode,
    documentToValidate,
    onContinue,
    selectedContactId,
    selectedContact,
  ]);

  return (
    <Form id={formId} onSubmit={handleSubmit}>
      {!contactsOpen && (
        <div className="mb-4">
          <PixTransferModeSelector value={transferMode} onChange={onTransferModeChange} />
        </div>
      )}

      <section className="pix-transfer-section app-card">
        {contactsOpen ? (
          <PixContactPickerPanel
            selectedId={selectedContactId}
            onBack={() => setContactsOpen(false)}
            onSelect={handleContactSelect}
          />
        ) : (
          <>
            <p className="pix-transfer-label">Nova transferência Pix</p>
            <h2 className="pix-transfer-section__title mt-1">
              {isKeyMode ? "Para quem você quer enviar?" : "Cole o código Pix"}
            </h2>

            {isKeyMode ? (
              <div className="mt-5 space-y-4">
                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="pix-transfer-label">Tipo de chave</p>
                    <PixContactPickerTrigger onOpen={() => setContactsOpen(true)} />
                  </div>
                  <div
                    className="pix-transfer-key-types"
                    role="radiogroup"
                    aria-label="Tipo de chave Pix"
                  >
                    {PIX_KEY_TYPE_OPTIONS.map((option) => {
                      const isActive = selectedKeyType === option.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          role="radio"
                          aria-checked={isActive}
                          onClick={() => handleKeyTypeChange(option.key)}
                          className={`pix-transfer-key-types__chip ${
                            isActive ? "pix-transfer-key-types__chip--active" : ""
                          }`}
                        >
                          {option.title}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="pix-transfer-label--field" htmlFor="pix-key-input">
                    Chave Pix
                  </label>
                  <div className="relative">
                    <input
                      id="pix-key-input"
                      type="text"
                      value={keyInput}
                      onChange={handleKeyChange}
                      placeholder={selectedTypeMeta.placeholder}
                      inputMode={
                        selectedKeyType === "cpf" ||
                        selectedKeyType === "cnpj" ||
                        selectedKeyType === "phone"
                          ? "numeric"
                          : "text"
                      }
                      className={`valut-input h-11 w-full rounded-xl ${showClearKey ? "!pr-10" : ""} ${keyError ? "!border-red-300 focus:!border-red-400" : ""}`}
                      autoComplete="off"
                    />
                    {showClearKey && (
                      <button
                        type="button"
                        onClick={handleClearKey}
                        className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                        aria-label="Limpar"
                      >
                        <TbX className="h-4 w-4" strokeWidth={2.25} />
                      </button>
                    )}
                  </div>
                  {keyError && <p className="mt-1.5 text-xs text-red-600">{keyError}</p>}
                </div>

                {selectedContact && (
                  <div className="pix-transfer-recipient">
                    <div className="pix-transfer-recipient__row">
                      <span className="pix-transfer-recipient__label">Favorecido</span>
                      <span className="pix-transfer-recipient__value">{selectedContact.name}</span>
                    </div>
                    <div className="pix-transfer-recipient__row">
                      <span className="pix-transfer-recipient__label">Instituição</span>
                      <span className="pix-transfer-recipient__value">{selectedContact.bankName}</span>
                    </div>
                  </div>
                )}

                <div>
                  <label className="pix-transfer-label--field" htmlFor="pix-holder-document">
                    CPF/CNPJ do titular (opcional)
                  </label>
                  <input
                    id="pix-holder-document"
                    type="text"
                    value={documentToValidate}
                    onChange={(e) => setDocumentToValidate(handleTaxIDInput(e.target.value))}
                    placeholder="Validação adicional de segurança"
                    className="valut-input h-11 rounded-xl"
                  />
                </div>
              </div>
            ) : (
              <div className="mt-5 space-y-3">
                <label className="pix-transfer-label--field" htmlFor="pix-copy-paste-input">
                  {intl.get("pix.transfer.copyPasteCodeLabel")}
                </label>
                <div className="pix-transfer-paste-field">
                  <textarea
                    id="pix-copy-paste-input"
                    name="qrCode"
                    value={qrCode}
                    onChange={(e) => {
                      setQrCode(e.target.value);
                      setKeyError("");
                    }}
                    placeholder={intl.get("pix.transfer.copyPastePlaceholder")}
                    rows={6}
                    autoComplete="off"
                    spellCheck={false}
                    disabled={decoding}
                    className={`valut-input min-h-[8rem] w-full resize-y rounded-xl font-mono text-xs leading-relaxed placeholder:text-gray-400 ${
                      keyError || decodeError ? "!border-red-300 focus:!border-red-400" : ""
                    }`}
                  />
                  <button
                    type="button"
                    onClick={handlePasteQrCode}
                    disabled={decoding}
                    className="pix-transfer-paste-btn"
                    aria-label={intl.get("pix.transfer.paste")}
                  >
                    <TbClipboardText className="h-3.5 w-3.5" strokeWidth={2.25} />
                    {intl.get("pix.transfer.paste")}
                  </button>
                </div>
                {keyError && <p className="mt-1.5 text-xs text-red-600">{keyError}</p>}
                {decodeError && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <p className="font-semibold">
                      {decodeError.title || "Não foi possível decodificar o código Pix"}
                    </p>
                    {decodeError.description ? (
                      <p className="mt-1 text-xs text-red-600">{decodeError.description}</p>
                    ) : null}
                  </div>
                )}
                <p className="text-xs leading-relaxed text-gray-500">
                  {intl.get("pix.transfer.copyPasteHint")}
                </p>
              </div>
            )}
          </>
        )}

        {!hideFooter && !contactsOpen && (
          <div className="mt-6 flex flex-col-reverse gap-2 border-t border-gray-100 pt-5 sm:flex-row sm:justify-between">
            <button
              type="button"
              className="btn-back w-full sm:w-auto"
              onClick={handleBack}
              disabled={decoding}
            >
              <TbArrowLeft className="h-4 w-4" />
              Voltar
            </button>
            <button type="submit" className="btn-pix-primary w-full sm:w-auto" disabled={decoding}>
              {decoding ? "Decodificando…" : "Continuar →"}
            </button>
          </div>
        )}
      </section>
    </Form>
  );
};
